import BridgeModelLogo from "@/components/site/BridgeModelLogo";
import ChicagoSkyline from "@/components/site/ChicagoSkyline";
import { useDeployment } from "@/lib/DeploymentContext";

const movements = [
  {
    label: "Movement 01 · The Lineage",
    period: "1960 — 2000",
    title: "Chicago's Pillar Community Service Program",
    body: [
      "The story does not begin in Texas. It begins in 1960, in Woodlawn, on the South Side of Chicago, with the founding of The Woodlawn Organization (TWO) — one of the most influential Black-led community organizations in American history. TWO was Chicago's pillar community-outreach institution: a coalition of churches, block clubs, and resident leaders that put the work of stabilizing families directly into the hands of the community itself.",
      "By the 1990s, TWO had stood up its housing-development arm — the Woodlawn Community Development Corporation (WCDC) — and was working hand-in-glove with the Chicago Housing Authority (CHA) to operate the very public-housing properties their residents lived in. The Service Connector framework — one trained caseworker, multi-year relationship, every service line — was forged inside that work. It became a federally-recognized blueprint, cited by HUD, replicated nationally, and tested over decades on some of the most distressed housing portfolios in the country.",
      "Anchoring all of it was Dr. Leon Finney Jr. — Chairman of TWO, Pastor of Metropolitan Apostolic Community Church, and one of the great living examples of what it looks like when civil-rights organizing, faith-based community outreach, and large-scale housing operations are run by the same set of hands.",
    ],
  },
  {
    label: "Movement 02 · The Formation",
    period: "1971 — 2000",
    title: "A product of the inner city.",
    body: [
      "I am a product of the inner city. A broken home. Separated from my mother at age nine. I left home at fifteen and I had to grow up fast. I have been working since I was fourteen.",
      "College was not an option I could see. But I knew I did not want to veer down a road of drugs and crime. So I worked. Through some really dark times I knew I wanted to make an impact, and I looked in many dark places looking for love, peace, comfort, security, and how to become a man.",
      "Thankfully, I landed in the presence of Dr. Leon Finney in 2000, at the age of twenty-nine. That is where this work begins for me.",
    ],
    isPersonal: true,
  },
  {
    label: "Movement 03 · The Apprenticeship",
    period: "2000 — 2007",
    title: "Pastor. Boss. Mentor. Godfather.",
    body: [
      "In 2000, I joined Metropolitan Apostolic Community Church under the pastorship of Dr. Leon Finney Jr. The church relationship came first; the professional and personal relationships grew from there. Dr. Finney became my pastor, my employer, my mentor, and ultimately my godfather — the formative voice on leadership, on community, and on the discipline of being in service.",
      "From April 2001 to May 2007, I worked under both TWO and WCDC simultaneously — as a Case Manager Service Connector responsible for a continuous 105-family caseload on CHA properties, and as a Community Outreach Leader running the church- and block-level organizing that fed the case-management pipeline. Six years. Two roles. One mentor.",
      "What I learned was not a curriculum. It was a posture. You don't manage families from behind a desk. You don't measure success by enrollments. You measure it by who is still standing, still housed, still working, still in school five years after you first met them. And you do the work because you were sent — by the community, by the church, by the people who trained you.",
    ],
  },
];

const getMovements = () => movements;

const getGoldenAgeBullets = (d) => [
  {
    label: "The post-COVID social-services collapse",
    body:
      "Every American municipality is staring at the same fragmentation Woodlawn faced sixty years ago — only now it spans senior care, mental health, workforce, housing, and childcare in parallel. The case for a single trained connector per family has never been stronger.",
  },
  {
    label: "A generational handoff is happening",
    body:
      "The first generation of civil-rights-era community organizers is passing on. The methods they built — high-touch, faith-rooted, multi-generational case management — have to be carried forward by practitioners they personally trained. There aren't many of us left.",
  },
  {
    label: "Texas is the right ground",
    body: d.contextSentence + " The Bridge Model doesn't need to create the opportunity. It just needs to walk families to it.",
  },
  {
    label: "Federal CDBG dollars are flowing now",
    body: d.fundingWindowSentence,
  },
];

export default function BridgeModelStory() {
  const d = useDeployment();
  const movements = getMovements(d);
  const goldenAgeBullets = getGoldenAgeBullets(d);
  return (
    <section
      id="story"
      data-testid="section-bridge-model-story"
      className="bg-[#0A192F] text-[#F9F7F1] py-24 lg:py-32 relative overflow-hidden"
    >
      <div className="absolute inset-0 grain-overlay opacity-30" aria-hidden />

      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-10">
        {/* Masthead with the logo */}
        <div className="flex flex-col items-center text-center mb-16">
          <BridgeModelLogo
            size={120}
            primary="#F9F7F1"
            accent="#D4AF37"
            sunGlow="#F4D77A"
            data-testid="story-logo-mark"
            className="mb-8"
          />
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-8 bg-[#D4AF37]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">
              About · The Bridge Model
            </span>
            <span className="h-px w-8 bg-[#D4AF37]" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-3xl">
            The Bridge Model <em className="italic text-[#D4AF37]">Story</em>
          </h2>
          <p className="text-[#CBD5E1] leading-relaxed mt-5 max-w-2xl">
            How a 65-year-old Chicago community-service tradition becomes
            {" "}{d.blueprintHeadline.replace(" — and the foundation of a new golden age of community service work.", "")} —
            and the foundation of a new golden age of municipal-service work across the country.
          </p>
        </div>

        {/* Two Movements */}
        <ol className="space-y-px bg-[#F9F7F1]/15 border border-[#F9F7F1]/15">
          {movements.map((m, i) => (
            <li
              key={m.label}
              data-testid={`story-movement-${i + 1}`}
              className={`bg-[#0A192F] p-8 lg:p-12 ${m.isPersonal ? "border-l-4 border-l-[#D4AF37] relative" : ""}`}
            >
              <div className="grid lg:grid-cols-12 gap-8">
                <div className="lg:col-span-4">
                  <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-2">
                    {m.label}
                  </div>
                  <div className="font-mono text-[11px] uppercase tracking-widest text-[#94A3B8] mb-3">
                    {m.period}
                  </div>
                  <h3
                    className={`font-display ${
                      m.isPersonal ? "italic text-2xl lg:text-3xl" : "text-xl lg:text-2xl"
                    } text-[#F9F7F1] leading-tight`}
                  >
                    {m.title}
                  </h3>
                  {m.isPersonal && (
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mt-4">
                      — In George's own words
                    </div>
                  )}
                </div>
                <div
                  className={`lg:col-span-8 space-y-4 leading-relaxed text-sm lg:text-base ${
                    m.isPersonal
                      ? "font-display italic text-[#F9F7F1] text-base lg:text-lg"
                      : "text-[#CBD5E1]"
                  }`}
                >
                  {m.body.map((p, j) => (
                    <p key={j}>{p}</p>
                  ))}
                </div>
              </div>
            </li>
          ))}
        </ol>

        {/* The Katrina Moment — pull quote centerpiece */}
        <div
          data-testid="story-katrina-quote"
          className="my-16 lg:my-20 relative"
        >
          <div className="bg-[#F9F7F1] text-[#0A192F] px-8 py-12 lg:px-14 lg:py-16 relative">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000] mb-4">
              The Katrina Moment · 2005
            </div>
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-5">
                <p className="text-sm lg:text-base text-[#475569] leading-relaxed">
                  When Hurricane Katrina struck the Gulf Coast in 2005, TWO and
                  WCDC stood up emergency housing for the transients and
                  refugees who arrived in Chicago. The work was overwhelming.
                  Many in the planning room were weary — uncertain whether the
                  effort could meaningfully matter to people who had lost
                  everything.
                </p>
                <p className="text-sm lg:text-base text-[#475569] leading-relaxed mt-4">
                  I took those doubts to Dr. Finney privately. What he said in
                  response became the operating philosophy of everything I have
                  built since:
                </p>
              </div>
              <div className="lg:col-span-7 lg:pl-8 lg:border-l lg:border-[#0A192F]/15 flex flex-col justify-center">
                <div className="font-display italic text-2xl lg:text-3xl xl:text-4xl text-[#0A192F] leading-snug">
                  <span className="text-[#8B0000] font-display text-5xl leading-none align-top mr-1">
                    &ldquo;
                  </span>
                  Whenever you encounter tragedy in our communities,
                  instead of complaining, always find a way to be in service of
                  others, and you will <em className="not-italic font-semibold">always</em> win.
                  <span className="text-[#8B0000] font-display text-5xl leading-none align-bottom ml-1">
                    &rdquo;
                  </span>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#475569] mt-5">
                  Dr. Leon Finney Jr.<br />
                  <span className="text-[#94A3B8]">
                    Chairman, The Woodlawn Organization · Pastor, Metropolitan
                    Apostolic Community Church
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Movement 04 · Golden Age */}
        <div
          data-testid="story-movement-4"
          className="bg-[#0A192F] border border-[#F9F7F1]/15 p-8 lg:p-12"
        >
          <div className="grid lg:grid-cols-12 gap-8 mb-10">
            <div className="lg:col-span-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-2">
                Movement 04 · The Inheritance
              </div>
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#94A3B8] mb-3">
                2026 →
              </div>
              <h3 className="font-display text-xl lg:text-2xl text-[#F9F7F1] leading-tight">
                A new golden age of community service work.
              </h3>
            </div>
            <div className="lg:col-span-8 space-y-4 text-[#CBD5E1] leading-relaxed text-sm lg:text-base">
              <p>
                Dr. Finney passed in 2020. The tradition he carried — Alinsky-era
                community organizing fused with faith-rooted pastoral
                leadership and large-scale housing operations — is now in the
                hands of the practitioners he personally trained. There aren't
                many of us. The model has to be rebuilt, formalized, and made
                replicable, or it disappears with the generation that built it.
              </p>
              <p>
                <strong className="text-[#F9F7F1]">
                  This is the right time to launch a fresh version of this
                  program.
                </strong>{" "}
                The convergence below is rare — it is the launch window for a
                decade-long expansion that can carry Gulf Coast communities into
                their golden age.
              </p>
            </div>
          </div>

          <ul className="grid md:grid-cols-2 gap-px bg-[#F9F7F1]/15 border border-[#F9F7F1]/15">
            {goldenAgeBullets.map((b, i) => (
              <li
                key={b.label}
                data-testid={`story-golden-age-${i}`}
                className="bg-[#0A192F] p-6 lg:p-7"
              >
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-2">
                  0{i + 1}
                </div>
                <div className="font-display text-base lg:text-lg text-[#F9F7F1] leading-snug mb-2">
                  {b.label}
                </div>
                <p className="text-xs lg:text-sm text-[#CBD5E1] leading-relaxed">
                  {b.body}
                </p>
              </li>
            ))}
          </ul>

          {/* Closing line */}
          <div className="mt-10 pt-8 border-t border-[#F9F7F1]/15">
            <p className="font-display italic text-lg lg:text-xl text-[#F9F7F1] leading-relaxed max-w-3xl">
              {d.closingCardTitle} — is not a
              proposal for a new social-services experiment. It is the
              continuation of a sixty-five-year tradition, carried forward by
              someone who was trained inside it, scaled for the community that needs
              it next.
            </p>
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mt-4">
              — George Broughton III · Proprietor &amp; Program Designer
            </p>
          </div>
        </div>

        {/* Chicago skyline — quiet visual signature of where the model was forged */}
        <div className="mt-20 lg:mt-24">
          <ChicagoSkyline
            height={120}
            fill="#F9F7F1"
            accent="#D4AF37"
            glow
            data-testid="story-chicago-skyline"
            className="opacity-80"
          />
          <div className="mt-4 flex items-center justify-center gap-3">
            <span className="h-px w-12 bg-[#D4AF37]/60" />
            <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">
              Chicago, IL · Where the methodology was forged
            </span>
            <span className="h-px w-12 bg-[#D4AF37]/60" />
          </div>
        </div>

        {/* In Memoriam · Dr. Leon Finney Jr. */}
        <div
          data-testid="story-in-memoriam"
          className="mt-20 lg:mt-24 max-w-3xl mx-auto"
        >
          <div className="bg-[#F9F7F1] text-[#0A192F] px-8 py-10 lg:px-12 lg:py-12 text-center relative">
            <span aria-hidden className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-4 bg-[#0A192F]">
              <span className="block font-mono text-[10px] uppercase tracking-[0.36em] text-[#D4AF37]">
                In Memoriam
              </span>
            </span>
            <div className="font-display italic text-base lg:text-lg text-[#475569] mb-5 leading-relaxed max-w-xl mx-auto">
              This work is dedicated to the memory of my pastor, my mentor, and
              my godfather.
            </div>
            <div className="font-display text-2xl lg:text-3xl text-[#0A192F] leading-snug">
              Dr. Leon Finney Jr.
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000] mt-2">
              1938 — 2020
            </div>
            <div className="mt-5 pt-5 border-t border-[#0A192F]/15 max-w-md mx-auto">
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#475569] leading-relaxed">
                Chairman · The Woodlawn Organization<br />
                Pastor · Metropolitan Apostolic Community Church<br />
                Chicago, Illinois
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
